import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { apiGet, apiPost } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { SearchSelect } from '@/components/composites/search-select'
import { ClientDialog } from '@/features/clients/ClientDialog'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { useWorkspace } from '@/features/workspace/WorkspaceProvider'
import { flattenTeams } from '@/features/teams'
import { newRowId, type Level3 } from '@/features/projects'
import { paths } from '@/routes/paths'
import { cn } from '@/lib/utils'
import type { Client, Geography, Member, Project, TeamNode } from '@/types/entities'
import { lifecycleApi, refreshCrmDrafts, type CrmDraft } from './api'
import {
  DEFAULT_TARGET_DAYS,
  PHASE_LABEL,
  PRODUCTS,
  PRODUCT_STATUSES,
  PRODUCT_STATUS_LABEL,
  TEAMS,
  TEAM_LONG,
  addDaysToDay,
  defaultPhaseDays,
  isoOfDay,
  type ContactRow,
  type OwnerTeam,
  type Phase,
  type ProductKey,
  type ProductStatus,
  type ScopeRow,
} from './model'

const STEPS = [
  { id: 'basics', label: 'Basics' },
  { id: 'people', label: 'People' },
  { id: 'requirements', label: 'Requirements' },
  { id: 'strategy', label: 'Strategy' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'plan', label: 'Plan' },
] as const
type Step = (typeof STEPS)[number]['id']
const LEVELS: Level3[] = ['low', 'medium', 'high']
const levelLabel = (l: string) => l[0]!.toUpperCase() + l.slice(1)

interface MetricRow { id: string; name: string; baseline: string; target: string; due: string }
interface MilestoneRow { id: string; name: string; date: string; phase: Phase | '' }
interface RiskRow { id: string; text: string; likelihood: Level3; impact: Level3; ownerTeam: OwnerTeam; mitigation: string }

interface FormState {
  name: string
  nameTouched: boolean
  clientId: string
  /** The client's region this project serves ('' = none): one Client Project per client and region. */
  geographyId?: string
  ownerId: string
  teamIds: string[]
  contacts: ContactRow[]
  ops: string[]
  pe: string[]
  products: Array<{ key: ProductKey; status: ProductStatus }>
  requirements: string
  problem: string
  aim: string
  metrics: MetricRow[]
  start: string
  skipPilot: boolean
  target: string
  targetTouched: boolean
  renewal: string
  phaseDays: Array<{ phase: 'pre_sales' | 'pilot' | 'implementation' | 'hypercare'; start: string; end: string }>
  phasesTouched: boolean
  scope: ScopeRow[]
  scopeNotes: string
  milestones: MilestoneRow[]
  risks: RiskRow[]
}

const today = () => new Date().toLocaleDateString('en-CA')
const contactRow = (): ContactRow => ({ id: newRowId(), name: '', role: '', email: '', decisionMaker: false, sponsor: false, left: false })

function emptyForm(ownerId: string): FormState {
  const start = today()
  return {
    name: '', nameTouched: false, clientId: '', ownerId, teamIds: [],
    contacts: [{ ...contactRow(), role: 'Sponsor', sponsor: true, decisionMaker: true }, { ...contactRow(), role: 'Day-to-day contact' }],
    ops: [], pe: [], products: [], requirements: '', problem: '', aim: '',
    metrics: [{ id: newRowId(), name: '', baseline: '', target: '', due: addDaysToDay(start, 35) }],
    start, skipPilot: false, target: addDaysToDay(start, DEFAULT_TARGET_DAYS), targetTouched: false, renewal: '',
    phaseDays: defaultPhaseDays(start, false), phasesTouched: false,
    scope: [{ id: newRowId(), text: '', inScope: true, addTask: true }], scopeNotes: '', milestones: [], risks: [],
  }
}

/** A form pre-filled from a GoHighLevel deal: the AM checks it and fills what the CRM does not know. */
function fromCrmDraft(d: CrmDraft, me: string): FormState {
  const base = emptyForm(d.owner.memberId ?? me)
  const at = d.enteredStageAt ?? d.createdAt
  const start = at ? new Date(at).toLocaleDateString('en-CA') : base.start
  // Only a remembered or identical name is pre-picked; a similar name is offered, never assumed.
  const firm = d.suggestions.find((s) => s.why !== 'similar name')
  return {
    ...base,
    clientId: firm?.id ?? '',
    geographyId: '',
    contacts: d.contacts.length
      ? d.contacts.map((c) => ({ ...contactRow(), name: c.name, email: c.email ?? '', role: c.primary ? 'Sponsor' : '', sponsor: c.primary, decisionMaker: c.primary }))
      : base.contacts,
    products: d.products.map((k) => ({ key: k as ProductKey, status: 'proposed' as ProductStatus })),
    requirements: d.description ?? '',
    problem: d.description ?? '',
    start,
    target: addDaysToDay(start, DEFAULT_TARGET_DAYS),
    phaseDays: defaultPhaseDays(start, false),
  }
}

/* ------------------------------------------------------------------ draft (this browser only) */

const draftKey = (userId: string, workspaceId: string) => `tapwork:client-project-draft:v1:${userId}:${workspaceId}`
function readDraft(key: string): FormState | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as FormState) : null
  } catch {
    return null
  }
}
function writeDraft(key: string, value: FormState | null) {
  try {
    if (value) localStorage.setItem(key, JSON.stringify(value))
    else localStorage.removeItem(key)
  } catch {
    // A draft is a convenience; a blocked store must never break the form.
  }
}

/* ------------------------------------------------------------------ validation */

function problemsOf(f: FormState, clientHasProject: boolean): Record<Step, string[]> {
  const out: Record<Step, string[]> = { basics: [], people: [], requirements: [], strategy: [], timeline: [], plan: [] }
  if (!f.name.trim()) out.basics.push('Give the project a name.')
  if (!f.clientId) out.basics.push('Pick the client, or add a new one.')
  if (clientHasProject) out.basics.push('This client already has a Client Project for this region. Add a new cycle to it instead, or pick another region.')
  if (!f.ownerId) out.basics.push('Pick the Account Manager who owns it.')
  const contacts = f.contacts.filter((c) => c.name.trim())
  if (!contacts.length) out.people.push('Name at least one client stakeholder.')
  if (contacts.some((c) => c.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email.trim()))) out.people.push('One of the stakeholder emails is not an email address.')
  if (!f.products.length) out.requirements.push('Pick at least one product.')
  if (!f.requirements.trim()) out.requirements.push('Say what the client needs from each product.')
  if (!f.problem.trim()) out.strategy.push("Write the client's problem.")
  if (!f.aim.trim()) out.strategy.push('Write the aim and strategy.')
  if (!f.metrics.some((m) => m.name.trim() && m.target.trim() !== '' && Number.isFinite(Number(m.target)))) out.strategy.push('Add at least one success metric with a numeric target.')
  if (f.metrics.some((m) => m.name.trim() && ((m.baseline.trim() !== '' && !Number.isFinite(Number(m.baseline))) || (m.target.trim() !== '' && !Number.isFinite(Number(m.target)))))) out.strategy.push('Baselines and targets are numbers.')
  if (!f.start) out.timeline.push('Pick the start date.')
  if (!f.target) out.timeline.push('Pick the target Post-Sales date.')
  if (f.start && f.target && f.target < f.start) out.timeline.push('The target Post-Sales date is before the start.')
  if (f.phaseDays.some((p) => !p.start || !p.end || p.end < p.start)) out.timeline.push('Every phase needs a start and an end on or after it.')
  if (!f.scope.some((s) => s.text.trim())) out.plan.push('List at least one scope item.')
  return out
}

/* ------------------------------------------------------------------ dialog */

/**
 * Open a Client Project (handbook: Creating a client project). One form, six sections; the
 * dashboard — phases, Gantt, action registry — is generated from it on Create project. Internal
 * work keeps the light project form, which "Internal" hands over to.
 */
export function NewClientProjectDialog({ open, onOpenChange, onChooseInternal, fromCrm = null }: { open: boolean; onOpenChange: (open: boolean) => void; onChooseInternal: () => void; fromCrm?: CrmDraft | null }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { workspaceSlug = '' } = useParams()
  const me = useIdentity()
  const { workspaceId } = useWorkspace()
  const key = draftKey(me.userId || 'me', workspaceId || 'ws')
  const [form, setForm] = useState<FormState>(() => (fromCrm ? fromCrmDraft(fromCrm, me.userId) : (readDraft(key) ?? emptyForm(me.userId))))
  const [step, setStep] = useState<Step>('basics')
  const [tried, setTried] = useState(false)
  const [saving, setSaving] = useState(false)
  const [addingClient, setAddingClient] = useState(false)
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }))

  const clientsQuery = useQuery({ queryKey: qk.clients(), queryFn: () => apiGet<{ items: Client[] }>('clients'), enabled: open })
  const membersQuery = useQuery({ queryKey: qk.members(), queryFn: () => apiGet<{ items: Member[] }>('members'), enabled: open })
  const projectsQuery = useQuery({ queryKey: qk.projects(), queryFn: () => apiGet<{ items: Project[] }>('projects'), enabled: open })
  const teamsQuery = useQuery({ queryKey: qk.teamTree(), queryFn: () => apiGet<{ tree: TeamNode[] }>('teams', { tree: true }), enabled: open, staleTime: 5 * 60_000 })

  const clients = useMemo(() => (clientsQuery.data?.items ?? []).filter((c) => !(c as Client & { archived?: boolean }).archived), [clientsQuery.data])
  const members = useMemo(() => (membersQuery.data?.items ?? []).filter((m) => !m.disabled), [membersQuery.data])
  const memberOptions = members.map((m) => ({ id: m.id, label: m.displayName, hint: m.email, photoUrl: m.photoUrl }))
  const teams = useMemo(() => flattenTeams(teamsQuery.data?.tree ?? []), [teamsQuery.data])
  const client = clients.find((c) => c.id === form.clientId)
  const geographyId = form.geographyId ?? ''
  const geographiesQuery = useQuery({
    queryKey: qk.geographies(form.clientId || undefined),
    queryFn: () => apiGet<{ items: Geography[] }>('geographies', { clientId: form.clientId }).then((r) => r.items),
    enabled: open && Boolean(form.clientId),
    staleTime: 60_000,
  })
  const geographies = geographiesQuery.data ?? []
  const region = geographies.find((g) => g.id === geographyId)
  const [newRegion, setNewRegion] = useState<string | null>(null)
  const addRegion = useMutation({
    mutationFn: (regionName: string) => apiPost<Geography>('geographies', { clientId: form.clientId, name: regionName, mode: 'virtual', address: null }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['geographies'] })
      setForm((f) => ({ ...f, geographyId: created.id }))
      setNewRegion(null)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
  // One Client Project per client AND region.
  const clientHasProject = Boolean(
    form.clientId && (projectsQuery.data?.items ?? []).some((p) => p.type === 'client' && p.clientId === form.clientId && (p.geographyId ?? '') === geographyId && !p.archived),
  )
  // The name follows the client (and region) until somebody types their own.
  const name = form.nameTouched ? form.name : client ? (region ? `${client.name} – ${region.name}` : `${client.name} – Client Project`) : ''
  const problems = problemsOf({ ...form, name }, clientHasProject)
  const stepHasProblems = (s: Step) => problems[s].length > 0

  const relayout = (start: string, skipPilot: boolean) =>
    setForm((f) => ({
      ...f,
      start,
      skipPilot,
      phaseDays: defaultPhaseDays(start, skipPilot),
      phasesTouched: false,
      target: f.targetTouched ? f.target : addDaysToDay(start, DEFAULT_TARGET_DAYS - (skipPilot ? 28 : 0)),
    }))

  const saveDraft = () => {
    writeDraft(key, form)
    toast.success('Draft saved in this browser')
  }

  const submit = async () => {
    setTried(true)
    const first = STEPS.find((s) => stepHasProblems(s.id))
    if (first) {
      setStep(first.id)
      return
    }
    setSaving(true)
    try {
      const dash = await lifecycleApi.create({
        name: name.trim(),
        clientId: form.clientId,
        geographyId: geographyId || null,
        ownerId: form.ownerId,
        teamIds: form.teamIds,
        startAt: isoOfDay(form.start),
        targetPostSalesAt: isoOfDay(form.target),
        renewalAt: form.renewal ? isoOfDay(form.renewal) : null,
        skipPilot: form.skipPilot,
        phaseDates: form.phasesTouched ? form.phaseDays.map((p) => ({ phase: p.phase, startAt: isoOfDay(p.start), endAt: isoOfDay(p.end) })) : [],
        products: form.products,
        requirements: form.requirements.trim(),
        problem: form.problem.trim(),
        contacts: form.contacts.filter((c) => c.name.trim()).map((c) => ({ ...c, name: c.name.trim(), role: c.role.trim(), email: c.email.trim() })),
        team: { ops: form.ops, pe: form.pe },
        scope: form.scope.filter((s) => s.text.trim()).map((s) => ({ ...s, text: s.text.trim() })),
        plan: {
          aim: form.aim.trim(),
          scopeIn: form.scopeNotes.trim() || null,
          metrics: form.metrics
            .filter((m) => m.name.trim())
            .map((m) => ({
              id: m.id,
              name: m.name.trim(),
              unit: '',
              baseline: m.baseline.trim() === '' ? null : Number(m.baseline),
              target: m.target.trim() === '' ? null : Number(m.target),
              current: m.baseline.trim() === '' ? null : Number(m.baseline),
              dueAt: m.due ? isoOfDay(m.due) : null,
            })),
          milestones: form.milestones.filter((m) => m.name.trim()).map((m) => ({ id: m.id, name: m.name.trim(), dueAt: m.date ? isoOfDay(m.date) : null, doneAt: null, phase: m.phase || null })),
          risks: form.risks
            .filter((r) => r.text.trim())
            .map((r) => ({ id: r.id, text: r.text.trim(), severity: r.impact, mitigation: r.mitigation.trim(), status: 'open', likelihood: r.likelihood, impact: r.impact, ownerTeam: r.ownerTeam })),
        },
        crm: fromCrm ? { dealId: fromCrm.dealId, companyKey: fromCrm.company.key } : null,
      })
      if (fromCrm) refreshCrmDrafts(qc)
      else writeDraft(key, null)
      void qc.invalidateQueries({ queryKey: qk.projects() })
      void qc.invalidateQueries({ queryKey: qk.lifecycles() })
      qc.setQueryData(qk.lifecycle(dash.project.id), dash)
      toast.success('Client Project created — phases and action registry are ready')
      onOpenChange(false)
      setForm(emptyForm(me.userId))
      setStep('basics')
      setTried(false)
      navigate(paths.clientProject(workspaceSlug, dash.project.id))
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  const shown = tried ? problems[step] : []
  const stepIndex = STEPS.findIndex((s) => s.id === step)

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="flex max-h-[92dvh] flex-col gap-0 p-0 sm:max-w-4xl">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle>New project</DialogTitle>
          <DialogDescription>One Client Project per client and region, from discovery to offboarding. Its dashboard is built from these answers.</DialogDescription>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 md:grid-cols-[12rem_minmax(0,1fr)]">
          <nav aria-label="Form sections" className="flex gap-1 overflow-x-auto border-b border-border p-3 md:flex-col md:border-b-0 md:border-r">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                type="button"
                aria-current={step === s.id}
                onClick={() => setStep(s.id)}
                className={cn('flex shrink-0 items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm', step === s.id ? 'bg-accent font-semibold text-brand' : 'text-muted-foreground hover:bg-accent/60')}
              >
                <span className={cn('grid size-5 place-items-center rounded-full text-tiny', tried && stepHasProblems(s.id) ? 'bg-danger-soft text-danger' : 'bg-surface-sunken')}>{i + 1}</span>
                {s.label}
              </button>
            ))}
          </nav>

          <div className="min-h-0 overflow-y-auto px-6 py-5">
            {step === 'basics' ? (
              <Section>
                {fromCrm ? (
                  <div className="rounded-lg border border-border bg-surface-sunken p-3 text-sm">
                    <p className="font-semibold">From GoHighLevel: {fromCrm.name}</p>
                    <p className="text-xs text-text-secondary">
                      {fromCrm.stage}
                      {fromCrm.owner.name ? ` · ${fromCrm.owner.name}` : ''}
                      {fromCrm.value ? ` · ${fromCrm.value.toLocaleString()}` : ''} · {fromCrm.company.name}. Stakeholders, products, description and the start date came from the deal; check them, and add what the CRM does not hold (region, renewal date, success metrics). Once created, the deal moves this project through Discovery and Pilot until Closed Won.
                    </p>
                  </div>
                ) : null}
                <Field label="Project type" required>
                  <div role="radiogroup" className="inline-flex rounded-md border border-border p-0.5">
                    <button type="button" role="radio" aria-checked="true" className="rounded px-3 py-1 text-sm font-semibold bg-brand text-text-inverse">Client Project</button>
                    <button type="button" role="radio" aria-checked="false" className="rounded px-3 py-1 text-sm text-muted-foreground hover:bg-accent" onClick={onChooseInternal}>Internal Project</button>
                  </div>
                  <Hint>A Client Project loads the seven phases, the dashboard and the action registry. Internal work uses the light form.</Hint>
                </Field>
                <Field label="Client" required>
                  <SearchSelect value={form.clientId} onChange={(v) => setForm((f) => ({ ...f, clientId: v, geographyId: '' }))} options={clients.map((c) => ({ id: c.id, label: c.name }))} placeholder="Search clients…" pending={clientsQuery.isPending} createLabel="Add a new client" onCreate={() => setAddingClient(true)} />
                  {fromCrm && fromCrm.suggestions.length && !form.clientId ? (
                    <div className="flex flex-wrap items-center gap-1.5 text-tiny text-text-secondary">
                      <span>Could be:</span>
                      {fromCrm.suggestions.map((sg) => (
                        <Button key={sg.id} type="button" size="sm" variant="outline" className="h-6 px-2 text-tiny" onClick={() => setForm((f) => ({ ...f, clientId: sg.id, geographyId: '' }))}>
                          {sg.name}
                        </Button>
                      ))}
                    </div>
                  ) : null}
                  {clientHasProject ? <p className="text-tiny text-danger">This client already has a Client Project for this region. A second contract or product is a new cycle inside it — open it and add a cycle.</p> : null}
                </Field>
                <Field label="Region">
                  <Select value={geographyId || '__none__'} onValueChange={(v) => setForm((f) => ({ ...f, geographyId: v === '__none__' ? '' : v }))} disabled={!form.clientId}>
                    <SelectTrigger><SelectValue placeholder="No region" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No region</SelectItem>
                      {geographies.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {form.clientId ? (
                    newRegion === null ? (
                      <Button type="button" size="sm" variant="ghost" className="h-7 justify-self-start px-2 text-xs" onClick={() => setNewRegion('')}><Plus className="size-3.5" /> Add a region</Button>
                    ) : (
                      <div className="flex gap-2">
                        <Input autoFocus aria-label="New region" placeholder="e.g. Philippines, LATAM, Egypt" value={newRegion} onChange={(e) => setNewRegion(e.target.value)} />
                        <Button type="button" size="sm" disabled={!newRegion.trim() || addRegion.isPending} onClick={() => addRegion.mutate(newRegion.trim())}>{addRegion.isPending ? 'Adding…' : 'Add'}</Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => setNewRegion(null)}>Cancel</Button>
                      </div>
                    )
                  ) : null}
                  <Hint>One Client Project per client and region: CNX Philippines and CNX Egypt are two. Leave it as No region when the client has only one.</Hint>
                </Field>
                <Field label="Project name" required>
                  <Input value={name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, nameTouched: true }))} placeholder="<Client> – Client Project" />
                </Field>
                <Field label="Project owner (Account Manager)" required>
                  <SearchSelect value={form.ownerId} onChange={(v) => set('ownerId', v)} options={memberOptions} placeholder="Search people…" pending={membersQuery.isPending} />
                  <Hint>The accountable AM. AM tasks are assigned to them.</Hint>
                </Field>
                <Field label="Teams delivering it">
                  <SearchSelect multiple value={form.teamIds} onChange={(v) => set('teamIds', v)} options={teams.map((t) => ({ id: t.id, label: t.label }))} placeholder="Search teams…" pending={teamsQuery.isPending} />
                  <Hint>Optional. Work items filed against the project may only go to these teams.</Hint>
                </Field>
              </Section>
            ) : null}

            {step === 'people' ? (
              <Section>
                <Field label="Client stakeholders" required hint="Sponsor and day-to-day contact at minimum.">
                  <div className="grid gap-2">
                    {form.contacts.map((c, i) => (
                      <div key={c.id} className="grid gap-2 rounded-lg border border-border p-2.5 sm:grid-cols-[1fr_1fr_1.2fr_auto]">
                        <Input aria-label="Name" placeholder="Name" value={c.name} onChange={(e) => set('contacts', form.contacts.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                        <Input aria-label="Role" placeholder="Role" value={c.role} onChange={(e) => set('contacts', form.contacts.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)))} />
                        <Input aria-label="Email" type="email" placeholder="Email" value={c.email} onChange={(e) => set('contacts', form.contacts.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))} />
                        <RemoveButton label="Remove stakeholder" onClick={() => set('contacts', form.contacts.filter((_, j) => j !== i))} />
                        <div className="flex flex-wrap gap-4 sm:col-span-4">
                          <CheckLabel checked={c.decisionMaker} onChange={(v) => set('contacts', form.contacts.map((x, j) => (j === i ? { ...x, decisionMaker: v } : x)))}>Decision-maker</CheckLabel>
                          <CheckLabel checked={c.sponsor} onChange={(v) => set('contacts', form.contacts.map((x, j) => (j === i ? { ...x, sponsor: v } : x)))}>Sponsor</CheckLabel>
                        </div>
                      </div>
                    ))}
                    <AddButton onClick={() => set('contacts', [...form.contacts, contactRow()])}>Add a stakeholder</AddButton>
                  </div>
                </Field>
                <Field label="TapTalent team · Operations & Support" hint="Account managers, helpdesk, Sales and Marketing; the pilot and implementation coordinators.">
                  <SearchSelect multiple value={form.ops} onChange={(v) => set('ops', v)} options={memberOptions} placeholder="Search people…" pending={membersQuery.isPending} />
                </Field>
                <Field label="TapTalent team · Product & Engineering" hint="Product leads, solution engineers and developers. The first person in each group gets that group's template tasks.">
                  <SearchSelect multiple value={form.pe} onChange={(v) => set('pe', v)} options={memberOptions} placeholder="Search people…" pending={membersQuery.isPending} />
                </Field>
              </Section>
            ) : null}

            {step === 'requirements' ? (
              <Section>
                <Field label="Products" required hint="Each product adds its standard set-up, training and go-live tasks to the action registry.">
                  <div className="grid gap-2 sm:grid-cols-2">
                    {PRODUCTS.map((p) => {
                      const row = form.products.find((x) => x.key === p.key)
                      return (
                        <div key={p.key} className={cn('flex items-center justify-between gap-2 rounded-lg border px-3 py-2', row ? 'border-brand bg-brand-softer' : 'border-border')}>
                          <CheckLabel checked={Boolean(row)} onChange={(v) => set('products', v ? [...form.products, { key: p.key, status: 'proposed' }] : form.products.filter((x) => x.key !== p.key))}>
                            <span className="font-medium">{p.label}</span>
                          </CheckLabel>
                          {row ? (
                            <Select value={row.status} onValueChange={(v) => set('products', form.products.map((x) => (x.key === p.key ? { ...x, status: v as ProductStatus } : x)))}>
                              <SelectTrigger className="h-7 w-32 text-xs" aria-label={`${p.label} status`}><SelectValue /></SelectTrigger>
                              <SelectContent>{PRODUCT_STATUSES.map((s) => <SelectItem key={s} value={s}>{PRODUCT_STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : null}
                        </div>
                      )
                    })}
                  </div>
                </Field>
                <Field label="Requirements detail" required hint="What the client needs from each product.">
                  <Textarea rows={8} value={form.requirements} onChange={(e) => set('requirements', e.target.value)} />
                </Field>
              </Section>
            ) : null}

            {step === 'strategy' ? (
              <Section>
                <Field label="Problem statement" required hint="The client's problem, in their words.">
                  <Textarea rows={4} value={form.problem} onChange={(e) => set('problem', e.target.value)} />
                </Field>
                <Field label="Aim and strategy" required hint="How TapTalent will solve it.">
                  <Textarea rows={4} value={form.aim} onChange={(e) => set('aim', e.target.value)} />
                </Field>
                <Field label="Success metrics" required hint="They drive the pilot's exit gate and the health score.">
                  <div className="grid gap-2">
                    <div className="hidden grid-cols-[2fr_1fr_1fr_1.2fr_auto] gap-2 text-tiny font-medium text-text-muted sm:grid">
                      <span>Metric</span><span>Baseline</span><span>Target</span><span>Due date</span><span className="w-8" />
                    </div>
                    {form.metrics.map((m, i) => (
                      <div key={m.id} className="grid gap-2 sm:grid-cols-[2fr_1fr_1fr_1.2fr_auto]">
                        <Input aria-label="Metric" placeholder="e.g. Time to hire (days)" value={m.name} onChange={(e) => set('metrics', form.metrics.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                        <Input aria-label="Baseline" inputMode="decimal" placeholder="Baseline" value={m.baseline} onChange={(e) => set('metrics', form.metrics.map((x, j) => (j === i ? { ...x, baseline: e.target.value } : x)))} />
                        <Input aria-label="Target" inputMode="decimal" placeholder="Target" value={m.target} onChange={(e) => set('metrics', form.metrics.map((x, j) => (j === i ? { ...x, target: e.target.value } : x)))} />
                        <Input aria-label="Due date" type="date" value={m.due} onChange={(e) => set('metrics', form.metrics.map((x, j) => (j === i ? { ...x, due: e.target.value } : x)))} />
                        <RemoveButton label="Remove metric" onClick={() => set('metrics', form.metrics.filter((_, j) => j !== i))} />
                      </div>
                    ))}
                    <AddButton onClick={() => set('metrics', [...form.metrics, { id: newRowId(), name: '', baseline: '', target: '', due: '' }])}>Add a metric</AddButton>
                  </div>
                </Field>
              </Section>
            ) : null}

            {step === 'timeline' ? (
              <Section>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Start date" required hint="The day discovery began (in the CRM: when the deal entered Pilot Discovery Booked).">
                    <Input type="date" value={form.start} onChange={(e) => e.target.value && relayout(e.target.value, form.skipPilot)} />
                  </Field>
                  <Field label="Target Post-Sales date" required hint="Default: start + 11 weeks. Health is measured against it.">
                    <Input type="date" value={form.target} onChange={(e) => setForm((f) => ({ ...f, target: e.target.value, targetTouched: true }))} />
                  </Field>
                  <Field label="Renewal date" hint="Optional. Renewal preparation is due 60 days before it.">
                    <Input type="date" value={form.renewal} onChange={(e) => set('renewal', e.target.value)} />
                  </Field>
                  <Field label="Pilot">
                    <label className="flex items-center gap-2 text-sm">
                      <Switch checked={form.skipPilot} onCheckedChange={(v) => relayout(form.start, v)} />
                      Skip the pilot (the client already uses the product)
                    </label>
                  </Field>
                </div>
                <Field label="Phase dates" hint="Auto-filled from the start date (1 / 4 / 4 / 2 weeks). Change any; Post-Sales follows Hypercare and stays open.">
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full min-w-[30rem] text-sm">
                      <thead className="bg-surface-sunken text-left text-tiny uppercase tracking-wide text-text-muted">
                        <tr><th className="px-3 py-2">Phase</th><th className="px-3 py-2">Start</th><th className="px-3 py-2">End</th></tr>
                      </thead>
                      <tbody>
                        {form.phaseDays.map((p, i) => (
                          <tr key={p.phase} className="border-t border-border-soft">
                            <td className="px-3 py-1.5 font-medium">{PHASE_LABEL[p.phase]}</td>
                            <td className="px-3 py-1.5"><Input aria-label={`${PHASE_LABEL[p.phase]} start`} type="date" className="h-8" value={p.start} onChange={(e) => setForm((f) => ({ ...f, phasesTouched: true, phaseDays: f.phaseDays.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)) }))} /></td>
                            <td className="px-3 py-1.5"><Input aria-label={`${PHASE_LABEL[p.phase]} end`} type="date" className="h-8" value={p.end} onChange={(e) => setForm((f) => ({ ...f, phasesTouched: true, phaseDays: f.phaseDays.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)) }))} /></td>
                          </tr>
                        ))}
                        <tr className="border-t border-border-soft text-muted-foreground">
                          <td className="px-3 py-1.5 font-medium">{PHASE_LABEL.post_sales}</td>
                          <td className="px-3 py-1.5" colSpan={2}>From {form.phaseDays.at(-1)?.end ?? '—'}, ongoing</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Field>
              </Section>
            ) : null}

            {step === 'plan' ? (
              <Section>
                <Field label="Scope" required hint="In-scope and out-of-scope items. Ticked in-scope items add a delivery task to Implementation.">
                  <div className="grid gap-2">
                    {form.scope.map((s, i) => (
                      <div key={s.id} className="grid items-center gap-2 sm:grid-cols-[1fr_auto_auto_auto]">
                        <Input aria-label="Scope item" placeholder="e.g. SSO with the client's Azure AD" value={s.text} onChange={(e) => set('scope', form.scope.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
                        <Select value={s.inScope ? 'in' : 'out'} onValueChange={(v) => set('scope', form.scope.map((x, j) => (j === i ? { ...x, inScope: v === 'in', addTask: v === 'in' ? x.addTask : false } : x)))}>
                          <SelectTrigger className="h-9 w-32" aria-label="In or out of scope"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="in">In scope</SelectItem><SelectItem value="out">Out of scope</SelectItem></SelectContent>
                        </Select>
                        <CheckLabel checked={s.addTask} disabled={!s.inScope} onChange={(v) => set('scope', form.scope.map((x, j) => (j === i ? { ...x, addTask: v } : x)))}>Add task</CheckLabel>
                        <RemoveButton label="Remove scope item" onClick={() => set('scope', form.scope.filter((_, j) => j !== i))} />
                      </div>
                    ))}
                    <AddButton onClick={() => set('scope', [...form.scope, { id: newRowId(), text: '', inScope: true, addTask: true }])}>Add a scope item</AddButton>
                    <Textarea rows={3} aria-label="Scope notes" placeholder="Scope notes (optional)" value={form.scopeNotes} onChange={(e) => set('scopeNotes', e.target.value)} />
                  </div>
                </Field>
                <Field label="Milestones" hint="Optional. The phase gates are added to the Gantt automatically.">
                  <div className="grid gap-2">
                    {form.milestones.map((m, i) => (
                      <div key={m.id} className="grid gap-2 sm:grid-cols-[2fr_1fr_1.2fr_auto]">
                        <Input aria-label="Milestone" placeholder="Milestone" value={m.name} onChange={(e) => set('milestones', form.milestones.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                        <Input aria-label="Date" type="date" value={m.date} onChange={(e) => set('milestones', form.milestones.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} />
                        <Select value={m.phase || 'none'} onValueChange={(v) => set('milestones', form.milestones.map((x, j) => (j === i ? { ...x, phase: v === 'none' ? '' : (v as Phase) } : x)))}>
                          <SelectTrigger aria-label="Phase"><SelectValue placeholder="Phase" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No phase</SelectItem>
                            {(['pre_sales', 'pilot', 'implementation', 'hypercare', 'post_sales'] as const).map((p) => <SelectItem key={p} value={p}>{PHASE_LABEL[p]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <RemoveButton label="Remove milestone" onClick={() => set('milestones', form.milestones.filter((_, j) => j !== i))} />
                      </div>
                    ))}
                    <AddButton onClick={() => set('milestones', [...form.milestones, { id: newRowId(), name: '', date: '', phase: '' }])}>Add a milestone</AddButton>
                  </div>
                </Field>
                <Field label="Risks" hint="Optional. Feeds the risk register; a high-impact risk with no mitigation turns the project yellow.">
                  <div className="grid gap-2">
                    {form.risks.map((r, i) => (
                      <div key={r.id} className="grid gap-2 rounded-lg border border-border p-2.5">
                        <div className="flex gap-2">
                          <Input aria-label="Risk" placeholder="Risk" value={r.text} onChange={(e) => set('risks', form.risks.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
                          <RemoveButton label="Remove risk" onClick={() => set('risks', form.risks.filter((_, j) => j !== i))} />
                        </div>
                        <div className="grid gap-2 sm:grid-cols-3">
                          <SmallSelect label="Likelihood" value={r.likelihood} options={LEVELS.map((l) => ({ value: l, label: levelLabel(l) }))} onChange={(v) => set('risks', form.risks.map((x, j) => (j === i ? { ...x, likelihood: v as Level3 } : x)))} />
                          <SmallSelect label="Impact" value={r.impact} options={LEVELS.map((l) => ({ value: l, label: levelLabel(l) }))} onChange={(v) => set('risks', form.risks.map((x, j) => (j === i ? { ...x, impact: v as Level3 } : x)))} />
                          <SmallSelect label="Owner team" value={r.ownerTeam} options={TEAMS.map((t) => ({ value: t, label: TEAM_LONG[t] }))} onChange={(v) => set('risks', form.risks.map((x, j) => (j === i ? { ...x, ownerTeam: v as OwnerTeam } : x)))} />
                        </div>
                        <Input aria-label="Mitigation" placeholder="Mitigation" value={r.mitigation} onChange={(e) => set('risks', form.risks.map((x, j) => (j === i ? { ...x, mitigation: e.target.value } : x)))} />
                      </div>
                    ))}
                    <AddButton onClick={() => set('risks', [...form.risks, { id: newRowId(), text: '', likelihood: 'medium', impact: 'medium', ownerTeam: 'am', mitigation: '' }])}>Add a risk</AddButton>
                  </div>
                </Field>
              </Section>
            ) : null}

            {shown.length ? (
              <ul role="alert" className="mt-4 list-disc rounded-lg bg-danger-soft py-2 pl-7 pr-3 text-sm text-danger">
                {shown.map((p) => <li key={p}>{p}</li>)}
              </ul>
            ) : null}
          </div>
        </div>

        <DialogFooter className="gap-2 border-t border-border px-6 py-3 sm:justify-between">
          <Button variant="ghost" onClick={saveDraft} disabled={saving}>Save draft</Button>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            {stepIndex > 0 ? <Button variant="outline" onClick={() => setStep(STEPS[stepIndex - 1]!.id)} disabled={saving}>Back</Button> : null}
            {stepIndex < STEPS.length - 1 ? (
              <Button variant="outline" onClick={() => setStep(STEPS[stepIndex + 1]!.id)}>Next</Button>
            ) : null}
            <Button onClick={() => void submit()} disabled={saving}>{saving ? 'Creating…' : 'Create project'}</Button>
          </div>
        </DialogFooter>

        <ClientDialog
          members={members}
          open={addingClient}
          onOpenChange={setAddingClient}
          onCreated={(created) => set('clientId', created.id)}
        />
      </DialogContent>
    </Dialog>
  )
}

/* ------------------------------------------------------------------ small parts */

function Section({ children }: { children: ReactNode }) {
  return <div className="grid gap-5">{children}</div>
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label>
        {label}
        {required ? <span className="ml-0.5 text-danger" aria-hidden>*</span> : null}
      </Label>
      {children}
      {hint ? <Hint>{hint}</Hint> : null}
    </div>
  )
}

function Hint({ children }: { children: ReactNode }) {
  return <p className="text-tiny text-text-muted">{children}</p>
}

function CheckLabel({ checked, onChange, disabled, children }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; children: ReactNode }) {
  return (
    <label className={cn('flex items-center gap-2 text-sm', disabled && 'opacity-50')}>
      <Checkbox checked={checked} disabled={disabled} onCheckedChange={(v) => onChange(v === true)} />
      {children}
    </label>
  )
}

function AddButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <Button type="button" variant="ghost" size="sm" className="h-7 justify-self-start gap-1 px-2 text-tiny text-brand" onClick={onClick}>
      <Plus className="size-3.5" />
      {children}
    </Button>
  )
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} className="grid size-9 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-danger">
      <Trash2 className="size-4" />
    </button>
  )
}

function SmallSelect({ label, value, options, onChange }: { label: string; value: string; options: Array<{ value: string; label: string }>; onChange: (v: string) => void }) {
  return (
    <div className="grid gap-1">
      <span className="text-tiny text-text-muted">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8" aria-label={label}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  )
}
